import { HttpClientModule } from '@angular/common/http';
import { NgModule, ErrorHandler, APP_INITIALIZER } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MissingTranslationHandler, TranslateModule } from '@ngx-translate/core';
import { CidaasHostedPageFooterModule } from 'cidaas-hosted-page-toolkit';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AccountDeduplicationComponent } from './components/pages/account-deduplication/account-deduplication.component';
import { ConsentPreviewComponent, SafePipe } from './components/pages/consent-preview/consent-preview.component';
import { ConsentScopesComponent } from './components/pages/consent-scopes/consent-scopes.component';
import { ErrorComponent } from './components/pages/error/error.component';
import { ForgotPasswordInitComponent } from './components/pages/forgot-password-init/forgot-password-init.component';
import { LoginSuccessComponent } from './components/pages/login-success/login-success.component';
import { LoginComponent } from './components/pages/login/login.component';
import { LogoutSuccessComponent } from './components/pages/logout-success/logout-success.component';
import { MfaRequiredComponent } from './components/pages/mfa-required/mfa-required.component';
import { PageNotFoundComponent } from './components/pages/page-not-found/page-not-found.component';
import { PasswSetSuccessComponent } from './components/pages/passw-set-success/passw-set-success.component';
import { PasswordSetComponent } from './components/pages/password-set/password-set.component';
import { RegisterAdditionalInfoComponent } from './components/pages/register-additional-info/register-additional-info.component';
import { RegisterSuccessComponent } from './components/pages/register-success/register-success.component';
import { RegisterComponent } from './components/pages/register/register.component';
import { VerificationCompleteComponent } from './components/pages/verification-complete/verification-complete.component';
import { VerificationInitComponent } from './components/pages/verification-init/verification-init.component';
import { FooterComponent } from './components/util/footer/footer.component';
import { ButtonComponent } from './components/util/form/button/button.component';
import { InputGroupComponent } from './components/util/form/input-group/input-group.component';
import { InputComponent } from './components/util/form/input/input.component';
import { HeaderComponent } from './components/util/header/header.component';
import { AdLoginProviderComponent } from './components/util/login/ad-login-provider/ad-login-provider.component';
import { AuthenticateComponent } from './components/util/login/authenticate/authenticate.component';
import { ClassicLoginProviderComponent } from './components/util/login/classic-login-provider/classic-login-provider.component';
import { CustomLoginProviderComponent } from './components/util/login/custom-login-provider/custom-login-provider.component';
import { GuestLoginComponent } from './components/util/login/guest-login/guest-login.component';
import { InitiateAuthenticationComponent } from './components/util/login/initiate-authentication/initiate-authentication.component';
import { InitiateLoginComponent } from './components/util/login/initiate-login/initiate-login.component';
import { SamlLoginProviderComponent } from './components/util/login/saml-login-provider/saml-login-provider.component';
import { SocialLoginProviderComponent } from './components/util/login/social-login-provider/social-login-provider.component';
import { AuthenticationSetupComponent } from './components/util/register/authentication-setup/authentication-setup.component';
import { DynamicFormModule } from './dynamic-form/dynamic-form.module';
import { Fido2Helper } from './helpers/fido2.helper';
import { RegexUtil } from './helpers/regex.helper';
import { MaterialModule } from './material/material.module';
import { CidaasService } from './service/cidaas.service';
import { IeTestService } from './service/ie-test.service';
import { SharedDataService } from './service/shared-data.service';
import { VideoComponent } from './components/util/video/video.component';
import { DeviceInitCodeComponent } from './components/util/device-code-flow/device-init-code/device-init-code.component';
import { DeviceSuccessPageComponent } from './components/util/device-code-flow/device-success-page/device-success-page.component';
import { HandleErrorComponent } from './components/util/error/handle-error/handle-error.component';
import { StatusComponent } from './components/pages/status/status.component';
import { GroupSelectionComponent } from './components/pages/group-selection/group-selection.component';
import { UnderScorePipe } from './pipe/under-score.pipe';
import { ErrorPipe } from './pipe/error.pipe';
import { PageLoaderComponent } from './components/util/cid-loader/page-loader/page-loader.component';
import { LoaderComponent } from './components/util/cid-loader/loader/loader.component';
import { PageLoaderDirective } from './components/util/cid-loader/page-loader.directive';
import { ContentLoaderComponent } from './components/util/cid-loader/content-loader/content-loader.component';
import { ContentLoaderDirective } from './components/util/cid-loader/content-loader.directive';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { Router } from '@angular/router';
import { CidMissingTranslationHandler } from './extensions/ngx-translate.extension';
import { ProvidersListComponent } from './components/util/login/providers-list/providers-list.component';
import { AppIconListComponent } from './components/util/app-icon-list/app-icon-list.component';
import { AppDialogComponent } from './components/util/app-dialog/app-dialog.component';
import { LoaderDirective } from './components/util/cid-loader/loader.directive';
import { SuggestVerificationMethodComponent } from './components/pages/suggest-verification-method/suggest-verification-method.component';
import { FilterPropertyPipe } from './pipe/filter-property.pipe';
import { UtilComponentsModule } from './shared/util-components/util-components.module';
import { VerificationModule } from './feature/verification/verification.module';


import { loggerConfig } from '../providers/logger.provider'; 
import { CidaasLoggerUiSdkModule } from 'cidaas-logger-ui-sdk'; 
 @NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    FooterComponent,
    ConsentPreviewComponent,
    HeaderComponent,
    PageNotFoundComponent,
    RegisterComponent,
    SocialLoginProviderComponent,
    ClassicLoginProviderComponent,
    CustomLoginProviderComponent,
    GuestLoginComponent,
    MfaRequiredComponent,
    GroupSelectionComponent,
    InitiateLoginComponent,
    InitiateAuthenticationComponent,
    AuthenticateComponent,
    InputComponent,
    ButtonComponent,
    InputGroupComponent,
    SamlLoginProviderComponent,
    AdLoginProviderComponent,
    SafePipe,
    ForgotPasswordInitComponent,
    ConsentScopesComponent,
    AccountDeduplicationComponent,
    RegisterSuccessComponent,
    LoginSuccessComponent,
    PasswSetSuccessComponent,
    ErrorComponent,
    LogoutSuccessComponent,
    VerificationInitComponent,
    VerificationCompleteComponent,
    RegisterAdditionalInfoComponent,
    AuthenticationSetupComponent,
    PasswordSetComponent,
    VideoComponent,
    HandleErrorComponent,
    DeviceInitCodeComponent,
    DeviceSuccessPageComponent,
    StatusComponent,
    UnderScorePipe,
    ErrorPipe,
    PageLoaderComponent,
    LoaderComponent,
    PageLoaderDirective,
    ContentLoaderComponent,
    ContentLoaderDirective,
    ProvidersListComponent,
    AppIconListComponent,
    AppDialogComponent,
    LoaderDirective,
    SuggestVerificationMethodComponent,
    FilterPropertyPipe
  ],
    imports: [
        CidaasLoggerUiSdkModule.forRoot(loggerConfig),
        BrowserModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        MaterialModule,
        ReactiveFormsModule,
        HttpClientModule,
        FlexLayoutModule,
        FormsModule,
        CidaasHostedPageFooterModule,
        TranslateModule.forRoot({
            missingTranslationHandler: {
                provide: MissingTranslationHandler,
                useClass: CidMissingTranslationHandler
            },
            defaultLanguage: 'en',
        }),
        DynamicFormModule,
        MatProgressSpinnerModule,
        UtilComponentsModule,
        VerificationModule
    ],
  providers: [
    CidaasService,
    SharedDataService,
    Fido2Helper,
    IeTestService,
    RegexUtil,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
